import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Orbit, Sparkles, Star, Zap } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type StageMeta = { status: string; stars?: number; note?: string }
type RalphProgress = {
  run_id: string
  idea: string
  status: string
  current_stage: string
  percent: number
  stars: number
  star_display: string
  pulse?: string
  build_id?: string
  stages: Record<string, StageMeta>
  ticks?: { tick_id: string; title: string; status: string }[]
  human_gates?: Record<string, string>
  timeline?: { at: string; stage: string; message: string }[]
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

function StarMeter({ stars, size = 28 }: { stars: number; size?: number }) {
  const items = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => {
      const threshold = i + 1
      if (stars >= threshold) return 'full'
      if (stars >= threshold - 0.5) return 'half'
      return 'empty'
    })
  }, [stars])
  return (
    <div className="flex items-center gap-1" aria-label={`${stars} of 5 stars`}>
      {items.map((kind, i) => (
        <motion.span
          key={i}
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: i * 0.05, type: 'spring', stiffness: 320 }}
          style={{ fontSize: size, lineHeight: 1 }}
          className={
            kind === 'empty'
              ? 'text-[var(--color-line)]'
              : 'text-[var(--color-amber)] drop-shadow-[0_0_12px_rgba(255,179,71,0.45)]'
          }
        >
          {kind === 'full' ? '★' : kind === 'half' ? '☆' : '✩'}
        </motion.span>
      ))}
    </div>
  )
}

export function RalphPage() {
  const { user, loading } = useAuth()
  const [idea, setIdea] = useState('Realtime fleet ops console with Stripe plans')
  const [progress, setProgress] = useState<RalphProgress | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [live, setLive] = useState(false)

  useEffect(() => {
    if (!progress?.run_id || !live) return
    const es = new EventSource(`/api/afos360/v1/ralph/${progress.run_id}/stream`, {
      withCredentials: true,
    })
    es.addEventListener('progress', (ev) => {
      try {
        setProgress(JSON.parse((ev as MessageEvent).data) as RalphProgress)
      } catch {
        /* ignore malformed */
      }
    })
    es.addEventListener('done', () => {
      setLive(false)
      es.close()
    })
    es.onerror = () => {
      setLive(false)
      es.close()
    }
    return () => es.close()
  }, [progress?.run_id, live])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function startRalph(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const op = await api<{ result: RalphProgress }>('/api/afos360/v1/ralph/start', {
        method: 'POST',
        body: JSON.stringify({ idea, max_ticks: 5, mode: 'dry_run' }),
      })
      setProgress(op.result)
      setLive(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralph failed to start')
    } finally {
      setBusy(false)
    }
  }

  async function decide(gate: 'certify_gate' | 'deploy_poc', decision: 'approve' | 'reject') {
    if (!progress?.run_id) return
    setBusy(true)
    setError(null)
    try {
      const row = await api<RalphProgress>(`/api/afos360/v1/ralph/${progress.run_id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ gate, decision, note: `${decision} from Ralph UI` }),
      })
      setProgress(row)
      setLive(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Decision failed')
    } finally {
      setBusy(false)
    }
  }

  const stageOrder = [
    'ideate',
    'specify',
    'storyboard',
    'iterate',
    'assemble',
    'certify_gate',
    'deploy_poc',
  ]

  return (
    <AppChrome>
      <main className="afos-page space-y-6">
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass overflow-hidden rounded-3xl p-4 sm:p-6"
        >
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <Star className="h-6 w-6 text-[var(--color-amber)]" />
            <h1 className="font-[family-name:var(--font-display)] text-2xl font-bold sm:text-3xl">
              Ralph
            </h1>
            {live && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(46,230,197,0.35)] bg-[rgba(46,230,197,0.08)] px-3 py-1 text-xs uppercase tracking-wider text-[var(--color-cyan)]">
                <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-cyan)]" />
                Live
              </span>
            )}
          </div>
          <p className="max-w-3xl text-sm text-[var(--color-mist)]">
            Highly modern autonomous loop: ideation → specify → storyboard → fresh-context
            ticks → assemble → certify → deploy POC. Realtime star status streams over SSE.
          </p>

          <form className="mt-5 flex flex-col gap-3 md:flex-row" onSubmit={startRalph}>
            <input
              className="flex-1 rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 outline-none focus:border-[var(--color-amber)]"
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              placeholder="What should we build?"
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--color-amber)] px-5 py-3 font-semibold text-[var(--color-void)]"
            >
              <Zap className="h-4 w-4" />
              Ignite Ralph
            </button>
          </form>
          {error && (
            <p className="mt-3 rounded-2xl border border-[rgba(255,107,138,0.35)] bg-[rgba(255,107,138,0.08)] px-3 py-2 text-sm text-[var(--color-rose)]">
              {error}
            </p>
          )}
        </motion.section>

        {progress && (
          <>
            <section className="glass rounded-3xl p-6">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[var(--color-mist)]">
                    Realtime build progress
                  </div>
                  <div className="mt-2 flex items-center gap-4">
                    <StarMeter stars={progress.stars} />
                    <div className="font-[family-name:var(--font-display)] text-4xl font-bold">
                      {progress.percent}%
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-[var(--color-cyan)]">{progress.pulse}</div>
                </div>
                <div className="text-right text-xs text-[var(--color-mist)]">
                  <div>{progress.run_id}</div>
                  <div>{progress.build_id}</div>
                  <div className="mt-1 uppercase tracking-wider text-[var(--color-amber)]">
                    {progress.status}
                  </div>
                </div>
              </div>
              <div className="mt-5 h-3 overflow-hidden rounded-full bg-black/40">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-[var(--color-amber)] via-[var(--color-cyan)] to-[var(--color-foam)]"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress.percent}%` }}
                  transition={{ type: 'spring', stiffness: 80, damping: 18 }}
                />
              </div>
            </section>

            <section className="grid gap-3 md:grid-cols-7">
              {stageOrder.map((stage) => {
                const meta = progress.stages?.[stage] || { status: 'pending' }
                const active = progress.current_stage === stage
                return (
                  <motion.div
                    key={stage}
                    layout
                    className={`rounded-2xl border px-3 py-3 ${
                      active
                        ? 'border-[var(--color-amber)] bg-[rgba(255,179,71,0.12)]'
                        : 'border-[var(--color-line)] bg-black/20'
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-[var(--color-mist)]">
                      {stage}
                    </div>
                    <div className="mt-1 text-lg text-[var(--color-amber)]">
                      {meta.status === 'passed'
                        ? '★★★★★'
                        : meta.status === 'awaiting_human'
                          ? '★★☆✩✩'
                          : meta.status === 'partial'
                            ? '★★★☆✩'
                            : '✩✩✩✩✩'}
                    </div>
                    <div className="text-[11px] text-[var(--color-foam)]">{meta.status}</div>
                  </motion.div>
                )
              })}
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <div className="glass rounded-3xl p-6">
                <h2 className="mb-3 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
                  <Orbit className="h-5 w-5 text-[var(--color-cyan)]" />
                  Fresh-context ticks
                </h2>
                <div className="space-y-2">
                  <AnimatePresence>
                    {(progress.ticks || []).map((tick) => (
                      <motion.div
                        key={tick.tick_id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-3 py-2 text-sm"
                      >
                        <span className="font-[family-name:var(--font-mono)] text-[var(--color-cyan)]">
                          {tick.tick_id}
                        </span>{' '}
                        {tick.title}
                        <span className="float-right text-xs uppercase text-[var(--color-mist)]">
                          {tick.status}
                        </span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {(progress.ticks || []).length === 0 && (
                    <p className="text-sm text-[var(--color-mist)]">Ticks will stream in live…</p>
                  )}
                </div>
              </div>

              <div className="glass rounded-3xl p-6">
                <h2 className="mb-3 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
                  <Sparkles className="h-5 w-5 text-[var(--color-amber)]" />
                  Human gates
                </h2>
                <p className="mb-4 text-sm text-[var(--color-mist)]">
                  Ralph pauses for named-human stars at certify and deploy POC.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={busy || progress.human_gates?.certify_gate !== 'awaiting_human'}
                    onClick={() => void decide('certify_gate', 'approve')}
                    className="rounded-full bg-[var(--color-foam)] px-4 py-2 text-sm font-semibold text-[var(--color-void)] disabled:opacity-40"
                  >
                    ★ Approve certify
                  </button>
                  <button
                    type="button"
                    disabled={busy || progress.human_gates?.deploy_poc !== 'awaiting_human'}
                    onClick={() => void decide('deploy_poc', 'approve')}
                    className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold disabled:opacity-40"
                  >
                    ★ Approve deploy POC
                  </button>
                </div>
                <div className="mt-4 max-h-48 space-y-1 overflow-auto text-xs text-[var(--color-mist)]">
                  {(progress.timeline || []).slice(-12).map((row, i) => (
                    <div key={`${row.at}-${i}`}>
                      <span className="text-[var(--color-cyan)]">{row.stage}</span> — {row.message}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </AppChrome>
  )
}
